import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Plug, Check, X, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ConnectorDef {
  id: string;
  connectorKey: string;
  name: string;
  description: string | null;
  category: string;
  isBuiltin: boolean;
  credentialSchema: string;
}

interface TenantIntegration {
  id: string;
  connectorId: string;
  connectorKey: string;
  connectorName: string;
  category: string;
  displayName: string | null;
  isActive: boolean;
  lastVerifiedAt: string | null;
  createdAt: string;
}

interface CredSchema {
  properties?: Record<string, { type?: string; description?: string }>;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function ConnectorIntegrationsPage() {
  const { t } = useTranslation();
  const [connectors, setConnectors] = useState<ConnectorDef[]>([]);
  const [integrations, setIntegrations] = useState<TenantIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [configureConnector, setConfigureConnector] = useState<ConnectorDef | null>(null);
  const [credentialFields, setCredentialFields] = useState<Record<string, string>>({});
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [connRes, intRes] = await Promise.all([
        apiClient.get<ConnectorDef[]>('/connectors'),
        apiClient.get<TenantIntegration[]>('/tenant-integrations'),
      ]);
      setConnectors(connRes.data);
      setIntegrations(intRes.data);
    } catch {
      // keep empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openConfigure(connector: ConnectorDef) {
    setConfigureConnector(connector);
    setCredentialFields({});
    setDisplayName('');
    try {
      const schema = JSON.parse(connector.credentialSchema) as CredSchema;
      const fields: Record<string, string> = {};
      Object.keys(schema.properties ?? {}).forEach((k) => { fields[k] = ''; });
      setCredentialFields(fields);
    } catch {
      // ignore parse errors
    }
  }

  async function save() {
    if (!configureConnector) return;
    setSaving(true);
    try {
      await apiClient.post('/tenant-integrations', {
        connectorId: configureConnector.id,
        displayName: displayName || null,
        credentials: credentialFields,
      });
      flash(t('integrations.saved'));
      setConfigureConnector(null);
      load();
    } catch {
      flash(t('integrations.error_save'));
    } finally {
      setSaving(false);
    }
  }

  async function remove(integrationId: string) {
    try {
      await apiClient.delete(`/tenant-integrations/${integrationId}`);
      flash(t('integrations.removed'));
      load();
    } catch {
      flash('Failed to remove integration');
    }
  }

  const integrationMap = new Map(integrations.map((i) => [i.connectorId, i]));

  const byCategory = connectors.reduce<Record<string, ConnectorDef[]>>((acc, c) => {
    (acc[c.category] = acc[c.category] ?? []).push(c);
    return acc;
  }, {});

  const categoryLabel = (cat: string): string =>
    t(`integrations.category_${cat.toLowerCase()}` as Parameters<typeof t>[0]) ?? cat;

  const isSecret = (field: string) =>
    ['key', 'token', 'secret', 'password'].some((s) => field.toLowerCase().includes(s));

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      {/* Configure dialog */}
      {configureConnector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{configureConnector.name}</h3>
              <button onClick={() => setConfigureConnector(null)}>
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{configureConnector.description}</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium">
                  {t('integrations.display_name')}
                </label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={configureConnector.name}
                />
              </div>
              {Object.entries(credentialFields).map(([field, value]) => (
                <div key={field}>
                  <label className="mb-1 block text-xs font-medium capitalize">
                    {field.replace(/_/g, ' ')}
                  </label>
                  <Input
                    type={isSecret(field) ? 'password' : 'text'}
                    value={value}
                    onChange={(e) =>
                      setCredentialFields((prev) => ({ ...prev, [field]: e.target.value }))
                    }
                    placeholder={field}
                  />
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfigureConnector(null)}>
                Cancel
              </Button>
              <Button onClick={save} disabled={saving}>
                {saving ? 'Saving...' : t('integrations.save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">{t('integrations.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('integrations.subtitle')}</p>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading...</p>}

      {/* Connectors by category */}
      {Object.entries(byCategory).map(([category, cats]) => (
        <div key={category} className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {categoryLabel(category)}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {cats.map((connector) => {
              const existing = integrationMap.get(connector.id);
              return (
                <div
                  key={connector.id}
                  className={cn(
                    'rounded-xl border bg-card p-4',
                    existing?.isActive && 'border-emerald-800',
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Plug className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <span className="text-sm font-medium">{connector.name}</span>
                        {connector.isBuiltin && (
                          <span className="ml-1.5 rounded-full bg-blue-900/40 px-1.5 py-0.5 text-[10px] font-medium text-blue-200">
                            {t('integrations.builtin_badge')}
                          </span>
                        )}
                      </div>
                    </div>
                    {existing ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-600">
                        <Check className="h-3 w-3" />
                        {t('integrations.connected')}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {t('integrations.not_configured')}
                      </span>
                    )}
                  </div>
                  {connector.description && (
                    <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
                      {connector.description}
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant={existing ? 'outline' : 'default'}
                      onClick={() => openConfigure(connector)}
                    >
                      {existing ? (
                        <>
                          <Plus className="mr-1 h-3 w-3" />
                          Update
                        </>
                      ) : (
                        t('integrations.configure')
                      )}
                    </Button>
                    {existing && (
                      <Button size="sm" variant="ghost" onClick={() => remove(existing.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!loading && connectors.length === 0 && (
        <div className="rounded-xl border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
          {t('integrations.empty')}
        </div>
      )}
    </div>
  );
}
